import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit';
import { ColorRing } from 'react-loader-spinner';
import API_BASE_URL from '../../../../Constants';

/** Confirmation for unsaved changes */
function CloseConfirmationDialog({ open, onConfirm, onCancel }) {
  return (
    <Dialog
      open={open}
      onClose={onCancel}
      aria-labelledby="close-confirm-title"
      aria-describedby="close-confirm-description"
      style={{ zIndex: 1600 }}
    >
      <DialogContent>
        <div className="flex flex-col items-center">
          <p className="text-lg font-semibold" id="close-confirm-title">
            Unsaved Changes
          </p>
          <p
            className="text-sm text-gray-600 mb-4"
            id="close-confirm-description"
          >
            Please save or cancel your changes before closing.
          </p>
          <div className="flex space-x-4">
            <button
              onClick={onCancel}
              className="px-4 py-2 bg-gray-300 font-bold rounded-md hover:bg-gray-400 transition"
            >
              Cancel
            </button>
            <button
              onClick={onConfirm}
              className="px-4 py-2 bg-blue-500 text-white font-bold rounded-md hover:bg-blue-600 transition"
            >
              Close Without Saving
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// Hard-coded array of NE states
const NE_STATES = ['CT','MA','ME','NH','NJ','NY','PA','RI','VT'];

// Hard-coded status options
const STATUS_OPTIONS = ['Active', 'Inactive'];

export default function ViewServiceCompanyModal({
  open,
  onClose,
  company,
  hasPrev = false,
  hasNext = false,
  setPrev,
  setNext,
  refreshCompanies,
}) {
  const [isEditMode, setIsEditMode] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [showCloseConfirm, setShowCloseConfirm] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  // Local copy for editing
  const [companyData, setCompanyData] = useState(() => ({
    serviceCompanyId: company?.serviceCompanyId || '',
    status: company?.status || '',
    type: company?.type || '',
    companyName: company?.companyName || '',
    // Address
    addressCareOf: company?.addressCareOf || '',
    addressLine1: company?.addressLine1 || '',
    addressLine2: company?.addressLine2 || '',
    city: company?.city || '',
    state: company?.state || '',
    zip: company?.zip || '',
    // Billing Address
    billingAddressCareOf: company?.billingAddressCareOf || '',
    billingAddressLine1: company?.billingAddressLine1 || '',
    billingAddressLine2: company?.billingAddressLine2 || '',
    billingCity: company?.billingCity || '',
    billingState: company?.billingState || '',
    billingZip: company?.billingZip || '',
    // Contact info
    contact: company?.contact || '',
    mainPhone: company?.mainPhone || '',
    otherPhone: company?.otherPhone || '',
    alternateOtherPhone: company?.alternateOtherPhone || '',
    fax: company?.fax || '',
    email: company?.email || '',
    otherEmail: company?.otherEmail || '',
    // Certification
    director: company?.director || '',
    companyCertificationNumber: company?.companyCertificationNumber || '',
    certificationByType: company?.certificationByType || '',
    certificationPhone: company?.certificationPhone || '',
    certificationEmail: company?.certificationEmail || '',
    certifiedDate: company?.certifiedDate || '',
    // Customer
    customer: company?.customer || '',
    customerID: company?.customerID || '',
  }));

  // If parent changes "company" while open => re-init
  useEffect(() => {
    if (company) {
      setCompanyData({
        serviceCompanyId: company.serviceCompanyId,
        status: company.status || '',
        type: company.type || '',
        companyName: company.companyName || '',
        addressCareOf: company.addressCareOf || '',
        addressLine1: company.addressLine1 || '',
        addressLine2: company.addressLine2 || '',
        city: company.city || '',
        state: company.state || '',
        zip: company.zip || '',
        billingAddressCareOf: company.billingAddressCareOf || '',
        billingAddressLine1: company.billingAddressLine1 || '',
        billingAddressLine2: company.billingAddressLine2 || '',
        billingCity: company.billingCity || '',
        billingState: company.billingState || '',
        billingZip: company.billingZip || '',
        contact: company.contact || '',
        mainPhone: company.mainPhone || '',
        otherPhone: company.otherPhone || '',
        alternateOtherPhone: company.alternateOtherPhone || '',
        fax: company.fax || '',
        email: company.email || '',
        otherEmail: company.otherEmail || '',
        director: company.director || '',
        companyCertificationNumber: company.companyCertificationNumber || '',
        certificationByType: company.certificationByType || '',
        certificationPhone: company.certificationPhone || '',
        certificationEmail: company.certificationEmail || '',
        certifiedDate: company.certifiedDate || '',
        customer: company.customer || '',
        customerID: company.customerID || '',
      });
      setIsEditMode(false);
    }
  }, [company]);

  // For toggling address vs billing
  const [activeAddressSection, setActiveAddressSection] = useState('address');

  // For required fields, etc.
  const [errors, setErrors] = useState({});

  // Real-time constraints in handleFieldChange
  function handleFieldChange(e) {
    let { name, value } = e.target;

    // If the field is status => user picks from dropdown
    if (name === 'status') {
      // direct set => no advanced constraints except required
      setCompanyData((prev) => ({ ...prev, status: value }));
      setErrors((prev) => ({ ...prev, status: '' }));
      return;
    }

    // If the field is state or billingState => from dropdown
    if (name === 'state' || name === 'billingState') {
      setCompanyData((prev) => ({ ...prev, [name]: value }));
      setErrors((prev) => ({ ...prev, [name]: '' }));
      return;
    }

    // Zip => only digits, max length 5
    if (name === 'zip' || name === 'billingZip') {
      // remove non-digit
      const numericVal = value.replace(/\D/g, '');
      if (numericVal.length > 5) {
        value = numericVal.slice(0,5);
      } else {
        value = numericVal;
      }
      setCompanyData((prev) => ({ ...prev, [name]: value }));
      setErrors((prev) => ({ ...prev, [name]: '' }));
      return;
    }

    // Phone => digits + dash, limit maybe 12 chars if you do "xxx-xxx-xxxx"
    // or 9 if "xxx-xxx-xxx" you said "9 characters with dashes"
    // We'll do "xxx-xxx-xxx" => 11 total length or 9? Let's do 11 total
    if (
      name === 'mainPhone' ||
      name === 'otherPhone' ||
      name === 'alternateOtherPhone' ||
      name === 'fax' ||
      name === 'certificationPhone'
    ) {
      // remove invalid chars
      const phoneVal = value.replace(/[^0-9-]/g, '');
      // limit length to e.g. 11
      if (phoneVal.length > 11) {
        value = phoneVal.slice(0, 11);
      } else {
        value = phoneVal;
      }
      setCompanyData((prev) => ({ ...prev, [name]: value }));
      setErrors((prev) => ({ ...prev, [name]: '' }));
      return;
    }

    // Email => minimal check with type="email" or do further checks on blur?
    // We'll do no real-time for email except clearing error
    // If user typed => store it
    if (name === 'email' || name === 'otherEmail' || name === 'certificationEmail') {
      setCompanyData((prev) => ({ ...prev, [name]: value }));
      setErrors((prev) => ({ ...prev, [name]: '' }));
      return;
    }

    // Date => type="date"? We'll store the string
    if (name === 'certifiedDate') {
      setCompanyData((prev) => ({ ...prev, [name]: value }));
      return;
    }

    // Else => normal text
    setCompanyData((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: '' }));
  }

  function validateFields() {
    const newErrors = {};
    // status => required
    if (!companyData.status) {
      newErrors.status = 'Status is required (Active or Inactive)';
    }
    // type => required
    if (!companyData.type) {
      newErrors.type = 'Type is required.';
    }
    // companyName => required
    if (!companyData.companyName) {
      newErrors.companyName = 'Company name is required';
    }
    return newErrors;
  }

  function handleClose() {
    if (isEditMode) {
      setShowCloseConfirm(true);
    } else {
      onClose();
    }
  }
  function confirmCloseWithoutSaving() {
    setShowCloseConfirm(false);
    setIsEditMode(false);
    onClose();
  }
  function cancelClose() {
    setShowCloseConfirm(false);
  }

  function handleToggleEditMode() {
    if (isEditMode) {
      handleSave();
    } else {
      setIsEditMode(true);
    }
  }

  function handleCancelEdit() {
    if (!company) return;
    // revert
    setCompanyData({
      serviceCompanyId: company.serviceCompanyId,
      status: company.status || '',
      type: company.type || '',
      companyName: company.companyName || '',
      addressCareOf: company.addressCareOf || '',
      addressLine1: company.addressLine1 || '',
      addressLine2: company.addressLine2 || '',
      city: company.city || '',
      state: company.state || '',
      zip: company.zip || '',
      billingAddressCareOf: company.billingAddressCareOf || '',
      billingAddressLine1: company.billingAddressLine1 || '',
      billingAddressLine2: company.billingAddressLine2 || '',
      billingCity: company.billingCity || '',
      billingState: company.billingState || '',
      billingZip: company.billingZip || '',
      contact: company.contact || '',
      mainPhone: company.mainPhone || '',
      otherPhone: company.otherPhone || '',
      alternateOtherPhone: company.alternateOtherPhone || '',
      fax: company.fax || '',
      email: company.email || '',
      otherEmail: company.otherEmail || '',
      director: company.director || '',
      companyCertificationNumber: company.companyCertificationNumber || '',
      certificationByType: company.certificationByType || '',
      certificationPhone: company.certificationPhone || '',
      certificationEmail: company.certificationEmail || '',
      certifiedDate: company.certifiedDate || '',
      customer: company.customer || '',
      customerID: company.customerID || '',
    });
    setIsEditMode(false);
  }

  async function handleSave() {
    const newErrors = validateFields();
    setErrors(newErrors);
    if (Object.keys(newErrors).length > 0) {
      alert('Please fix validation errors first.');
      return;
    }

    setIsLoading(true);
    try {
      const url = `${API_BASE_URL}/api/inspections/service-companies/${companyData.serviceCompanyId}`;
      const response = await fetch(url, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(companyData),
      });
      if (!response.ok) {
        throw new Error(`Update failed => ${response.status}`);
      }
      alert('Service company saved successfully.');
      setIsEditMode(false);
      if (refreshCompanies) {
        refreshCompanies();
      }
    } catch (err) {
      console.error('Error saving =>', err);
      alert(`Error => ${err.message}`);
    } finally {
      setIsLoading(false);
    }
  }

  async function handleDelete() {
    const confirm = window.confirm('Are you sure you want to delete?');
    if (!confirm) return;
    setIsLoading(true);
    try {
      const url = `${API_BASE_URL}/api/inspections/service-companies/${companyData.serviceCompanyId}`;
      const response = await fetch(url, {
        method: 'DELETE',
        credentials: 'include',
      });
      if (!response.ok) {
        throw new Error(`Delete failed => ${response.status}`);
      }
      alert('Service company deleted.');
      if (refreshCompanies) {
        refreshCompanies();
      }
      onClose();
    } catch (err) {
      console.error('Error deleting =>', err);
      alert(`Error deleting => ${err.message}`);
    } finally {
      setIsLoading(false);
    }
  }

  // Helper for error display
  function renderError(field) {
    if (!errors[field]) return null;
    return <p className="text-red-500 text-sm mt-1">{errors[field]}</p>;
  }

  // Renders a text input (with optional date/email type) or phone constraints
  function renderTextInput(fieldLabel, fieldName, required = false, type = 'text') {
    const val = companyData[fieldName] ?? '';
    return (
      <div className="mb-2">
        <label className="font-semibold text-sm mb-1 block">
          {fieldLabel}
          {required && <span className="text-red-500 ml-1">*</span>}
        </label>
        <input
          name={fieldName}
          type={type}
          value={val}
          onChange={handleFieldChange}
          disabled={!isEditMode}
          className="border border-gray-300 rounded p-1 w-full"
        />
        {renderError(fieldName)}
      </div>
    );
  }

  // Renders a dropdown for status or state
  function renderDropdown(fieldLabel, fieldName, options, required = false) {
    const val = companyData[fieldName] ?? '';
    return (
      <div className="mb-2">
        <label className="font-semibold text-sm mb-1 block">
          {fieldLabel}
          {required && <span className="text-red-500 ml-1">*</span>}
        </label>
        <select
          name={fieldName}
          value={val}
          onChange={handleFieldChange}
          disabled={!isEditMode}
          className="border border-gray-300 rounded p-1 w-full bg-white"
        >
          <option value="">-- Select --</option>
          {options.map((opt) => (
            <option key={opt} value={opt}>
              {opt}
            </option>
          ))}
        </select>
        {renderError(fieldName)}
      </div>
    );
  }

  // We'll do a toggle for address vs. billing
  return (
    <>
      <CloseConfirmationDialog
        open={showCloseConfirm}
        onConfirm={confirmCloseWithoutSaving}
        onCancel={cancelClose}
      />

      {open && !isEditMode && (
        <>
          {/* Arrows only if not editing */}
          <button
            onClick={setPrev}
            disabled={!hasPrev}
            style={{
              position: 'fixed',
              left: '5%',
              top: '50%',
              transform: 'translateY(-50%)',
              zIndex: 1500,
            }}
            className={`p-2 rounded-full bg-white hover:bg-gray-200 transition ${
              !hasPrev ? 'opacity-50 cursor-not-allowed' : ''
            }`}
          >
            <ArrowBackIcon />
          </button>
          <button
            onClick={setNext}
            disabled={!hasNext}
            style={{
              position: 'fixed',
              right: '5%',
              top: '50%',
              transform: 'translateY(-50%)',
              zIndex: 1500,
            }}
            className={`p-2 rounded-full bg-white hover:bg-gray-200 transition ${
              !hasNext ? 'opacity-50 cursor-not-allowed' : ''
            }`}
          >
            <ArrowForwardIcon />
          </button>
        </>
      )}

      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth={false}
        PaperProps={{
          style: {
            width: isExpanded ? '85%' : '700px',
            height: '85%',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
          },
        }}
        style={{ zIndex: 1400 }}
      >
        {/* Header */}
        <div className="sticky top-0 bg-white z-10 border-b border-gray-300">
          {/* Title row */}
          <div className="flex justify-between items-center p-4">
            <div className="flex items-center space-x-4 flex-grow">
              <button
                onClick={handleClose}
                className="p-2 rounded-full hover:bg-gray-200 transition"
                aria-label="Back"
              >
                <ArrowBackIcon />
              </button>
              <h2 className="text-2xl font-bold text-left">
                {isEditMode
                  ? `Edit Service Company #${companyData.serviceCompanyId}`
                  : `View Service Company #${companyData.serviceCompanyId}`}
              </h2>
            </div>
            {/* Expand Icon */}
            <button
              onClick={() => setIsExpanded(!isExpanded)}
              className="p-2 rounded-full hover:bg-gray-200 transition"
              aria-label={isExpanded ? 'Shrink' : 'Expand'}
            >
              {isExpanded ? <FullscreenExitIcon /> : <FullscreenIcon />}
            </button>
          </div>

          {/* Action buttons row */}
          <div className="flex space-x-4 p-4">
            {isEditMode ? (
              <>
                <button
                  onClick={handleCancelEdit}
                  className="px-4 py-2 bg-gray-300 text-black font-bold rounded-md hover:bg-gray-400 transition"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSave}
                  className="px-6 py-2 bg-yellow-500 text-white font-bold rounded-md hover:bg-yellow-600 transition"
                >
                  Save
                </button>
                <button
                  onClick={handleDelete}
                  className="px-6 py-2 bg-red-500 text-white font-bold rounded-md hover:bg-red-600 transition"
                >
                  Delete
                </button>
              </>
            ) : (
              <button
                onClick={handleToggleEditMode}
                className="px-6 py-2 bg-yellow-500 text-white font-bold rounded-md hover:bg-yellow-600 transition"
              >
                Edit
              </button>
            )}
          </div>
        </div>

        {/* Main Content */}
        <DialogContent dividers style={{ flex: '1 1 auto', overflow: 'auto' }}>
          {isLoading ? (
            <div className="flex justify-center items-center pt-24">
              <ColorRing
                visible
                height="80"
                width="80"
                colors={['#033B5C','#033B5C','#033B5C','#033B5C','#033B5C']}
              />
            </div>
          ) : (
            <div className="space-y-6">
              {/* 1) Top row => ID, Status, Type, CompanyName */}
              <div className="grid grid-cols-2 gap-4">
                {/* Service Company ID => read-only */}
                <div>
                  <label className="font-semibold text-sm mb-1 block">
                    Service Company ID
                  </label>
                  <input
                    name="serviceCompanyId"
                    value={companyData.serviceCompanyId || ''}
                    disabled
                    className="border border-gray-300 rounded p-1 w-full bg-gray-100"
                  />
                </div>

                {/* Status => dropdown */}
                {renderDropdown('Status','status', STATUS_OPTIONS, true)}

                {/* Type => required => normal text input */}
                <div>
                  <label className="font-semibold text-sm mb-1 block">
                    Type <span className="text-red-500 ml-1">*</span>
                  </label>
                  <input
                    name="type"
                    value={companyData.type}
                    onChange={handleFieldChange}
                    disabled={!isEditMode}
                    className="border border-gray-300 rounded p-1 w-full"
                  />
                  {renderError('type')}
                </div>

                {/* Company Name => required */}
                <div>
                  <label className="font-semibold text-sm mb-1 block">
                    Company Name <span className="text-red-500 ml-1">*</span>
                  </label>
                  <input
                    name="companyName"
                    value={companyData.companyName}
                    onChange={handleFieldChange}
                    disabled={!isEditMode}
                    className="border border-gray-300 rounded p-1 w-full"
                  />
                  {renderError('companyName')}
                </div>
              </div>

              {/* 2) Address => toggle for normal vs. billing */}
              <div className="border-t border-gray-300 pt-3">
                <h3 className="font-bold text-md mb-2">Address</h3>

                {/* Toggle buttons */}
                <div className="flex space-x-4 mb-4">
                  <button
                    type="button"
                    onClick={() => setActiveAddressSection('address')}
                    className={`px-4 py-2 rounded-md font-semibold ${
                      activeAddressSection === 'address'
                        ? 'bg-blue-500 text-white'
                        : 'bg-gray-200 text-gray-700'
                    }`}
                  >
                    Address
                  </button>
                  <button
                    type="button"
                    onClick={() => setActiveAddressSection('billing')}
                    className={`px-4 py-2 rounded-md font-semibold ${
                      activeAddressSection === 'billing'
                        ? 'bg-blue-500 text-white'
                        : 'bg-gray-200 text-gray-700'
                    }`}
                  >
                    Billing Address
                  </button>
                </div>

                {activeAddressSection === 'address' && (
                  <div className="grid grid-cols-2 gap-4">
                    {/* addressCareOf, addressLine1/2, city, state => dropdown, zip => numeric */}
                    <div>
                      <label className="font-semibold text-sm mb-1 block">
                        Address Care Of
                      </label>
                      <input
                        name="addressCareOf"
                        value={companyData.addressCareOf}
                        onChange={handleFieldChange}
                        disabled={!isEditMode}
                        className="border border-gray-300 rounded p-1 w-full"
                      />
                    </div>
                    <div>
                      <label className="font-semibold text-sm mb-1 block">
                        Address Line 1
                      </label>
                      <input
                        name="addressLine1"
                        value={companyData.addressLine1}
                        onChange={handleFieldChange}
                        disabled={!isEditMode}
                        className="border border-gray-300 rounded p-1 w-full"
                      />
                    </div>
                    <div>
                      <label className="font-semibold text-sm mb-1 block">
                        Address Line 2
                      </label>
                      <input
                        name="addressLine2"
                        value={companyData.addressLine2}
                        onChange={handleFieldChange}
                        disabled={!isEditMode}
                        className="border border-gray-300 rounded p-1 w-full"
                      />
                    </div>
                    {/* State => dropdown */}
                    {renderDropdown('State','state',NE_STATES)}
                    <div>
                      <label className="font-semibold text-sm mb-1 block">
                        City
                      </label>
                      <input
                        name="city"
                        value={companyData.city}
                        onChange={handleFieldChange}
                        disabled={!isEditMode}
                        className="border border-gray-300 rounded p-1 w-full"
                      />
                    </div>
                    <div>
                      <label className="font-semibold text-sm mb-1 block">
                        ZIP
                      </label>
                      <input
                        name="zip"
                        value={companyData.zip}
                        onChange={handleFieldChange}
                        disabled={!isEditMode}
                        className="border border-gray-300 rounded p-1 w-full"
                      />
                    </div>
                  </div>
                )}

                {activeAddressSection === 'billing' && (
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="font-semibold text-sm mb-1 block">
                        Billing Care Of
                      </label>
                      <input
                        name="billingAddressCareOf"
                        value={companyData.billingAddressCareOf}
                        onChange={handleFieldChange}
                        disabled={!isEditMode}
                        className="border border-gray-300 rounded p-1 w-full"
                      />
                    </div>
                    <div>
                      <label className="font-semibold text-sm mb-1 block">
                        Billing Line 1
                      </label>
                      <input
                        name="billingAddressLine1"
                        value={companyData.billingAddressLine1}
                        onChange={handleFieldChange}
                        disabled={!isEditMode}
                        className="border border-gray-300 rounded p-1 w-full"
                      />
                    </div>
                    <div>
                      <label className="font-semibold text-sm mb-1 block">
                        Billing Line 2
                      </label>
                      <input
                        name="billingAddressLine2"
                        value={companyData.billingAddressLine2}
                        onChange={handleFieldChange}
                        disabled={!isEditMode}
                        className="border border-gray-300 rounded p-1 w-full"
                      />
                    </div>
                    {renderDropdown('Billing State','billingState',NE_STATES)}
                    <div>
                      <label className="font-semibold text-sm mb-1 block">
                        Billing City
                      </label>
                      <input
                        name="billingCity"
                        value={companyData.billingCity}
                        onChange={handleFieldChange}
                        disabled={!isEditMode}
                        className="border border-gray-300 rounded p-1 w-full"
                      />
                    </div>
                    <div>
                      <label className="font-semibold text-sm mb-1 block">
                        Billing ZIP
                      </label>
                      <input
                        name="billingZip"
                        value={companyData.billingZip}
                        onChange={handleFieldChange}
                        disabled={!isEditMode}
                        className="border border-gray-300 rounded p-1 w-full"
                      />
                    </div>
                  </div>
                )}
              </div>

              {/* 3) Contact / Phones */}
              <div className="border-t border-gray-300 pt-3">
                <h3 className="font-bold text-md mb-2">Contact & Phones</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="font-semibold text-sm mb-1 block">
                      Contact
                    </label>
                    <input
                      name="contact"
                      value={companyData.contact}
                      onChange={handleFieldChange}
                      disabled={!isEditMode}
                      className="border border-gray-300 rounded p-1 w-full"
                    />
                  </div>
                  {/* mainPhone => digits + dash => up to 11 */}
                  <div>
                    <label className="font-semibold text-sm mb-1 block">
                      Main Phone
                    </label>
                    <input
                      name="mainPhone"
                      value={companyData.mainPhone}
                      onChange={handleFieldChange}
                      disabled={!isEditMode}
                      className="border border-gray-300 rounded p-1 w-full"
                    />
                  </div>
                  <div>
                    <label className="font-semibold text-sm mb-1 block">
                      Other Phone
                    </label>
                    <input
                      name="otherPhone"
                      value={companyData.otherPhone}
                      onChange={handleFieldChange}
                      disabled={!isEditMode}
                      className="border border-gray-300 rounded p-1 w-full"
                    />
                  </div>
                  <div>
                    <label className="font-semibold text-sm mb-1 block">
                      Alternate Other Phone
                    </label>
                    <input
                      name="alternateOtherPhone"
                      value={companyData.alternateOtherPhone}
                      onChange={handleFieldChange}
                      disabled={!isEditMode}
                      className="border border-gray-300 rounded p-1 w-full"
                    />
                  </div>
                  <div>
                    <label className="font-semibold text-sm mb-1 block">
                      Fax
                    </label>
                    <input
                      name="fax"
                      value={companyData.fax}
                      onChange={handleFieldChange}
                      disabled={!isEditMode}
                      className="border border-gray-300 rounded p-1 w-full"
                    />
                  </div>
                  {/* email => type="email"? We'll do minimal real-time checks */}
                  <div>
                    <label className="font-semibold text-sm mb-1 block">
                      Email
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={companyData.email}
                      onChange={handleFieldChange}
                      disabled={!isEditMode}
                      className="border border-gray-300 rounded p-1 w-full"
                    />
                  </div>
                  <div>
                    <label className="font-semibold text-sm mb-1 block">
                      Other Email
                    </label>
                    <input
                      type="email"
                      name="otherEmail"
                      value={companyData.otherEmail}
                      onChange={handleFieldChange}
                      disabled={!isEditMode}
                      className="border border-gray-300 rounded p-1 w-full"
                    />
                  </div>
                </div>
              </div>

              {/* 4) Certification */}
              <div className="border-t border-gray-300 pt-3">
                <h3 className="font-bold text-md mb-2">Certification</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="font-semibold text-sm mb-1 block">
                      Director
                    </label>
                    <input
                      name="director"
                      value={companyData.director}
                      onChange={handleFieldChange}
                      disabled={!isEditMode}
                      className="border border-gray-300 rounded p-1 w-full"
                    />
                  </div>
                  <div>
                    <label className="font-semibold text-sm mb-1 block">
                      Company Cert #
                    </label>
                    <input
                      name="companyCertificationNumber"
                      value={companyData.companyCertificationNumber}
                      onChange={handleFieldChange}
                      disabled={!isEditMode}
                      className="border border-gray-300 rounded p-1 w-full"
                    />
                  </div>
                  <div>
                    <label className="font-semibold text-sm mb-1 block">
                      Certification By/Type
                    </label>
                    <input
                      name="certificationByType"
                      value={companyData.certificationByType}
                      onChange={handleFieldChange}
                      disabled={!isEditMode}
                      className="border border-gray-300 rounded p-1 w-full"
                    />
                  </div>
                  {/* certificationPhone => same phone constraints */}
                  <div>
                    <label className="font-semibold text-sm mb-1 block">
                      Certification Phone
                    </label>
                    <input
                      name="certificationPhone"
                      value={companyData.certificationPhone}
                      onChange={handleFieldChange}
                      disabled={!isEditMode}
                      className="border border-gray-300 rounded p-1 w-full"
                    />
                  </div>
                  {/* certificationEmail => type="email" */}
                  <div>
                    <label className="font-semibold text-sm mb-1 block">
                      Certification Email
                    </label>
                    <input
                      type="email"
                      name="certificationEmail"
                      value={companyData.certificationEmail}
                      onChange={handleFieldChange}
                      disabled={!isEditMode}
                      className="border border-gray-300 rounded p-1 w-full"
                    />
                  </div>
                  {/* date => type="date" */}
                  <div>
                    <label className="font-semibold text-sm mb-1 block">
                      Certified Date
                    </label>
                    <input
                      type="date"
                      name="certifiedDate"
                      value={companyData.certifiedDate}
                      onChange={handleFieldChange}
                      disabled={!isEditMode}
                      className="border border-gray-300 rounded p-1 w-full"
                    />
                  </div>
                </div>
              </div>

              {/* 5) Customer Info */}
              <div className="border-t border-gray-300 pt-3">
                <h3 className="font-bold text-md mb-2">Customer Info</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="font-semibold text-sm mb-1 block">
                      Customer
                    </label>
                    <input
                      name="customer"
                      value={companyData.customer}
                      onChange={handleFieldChange}
                      disabled={!isEditMode}
                      className="border border-gray-300 rounded p-1 w-full"
                    />
                  </div>
                  <div>
                    <label className="font-semibold text-sm mb-1 block">
                      Customer ID
                    </label>
                    <input
                      name="customerID"
                      value={companyData.customerID}
                      onChange={handleFieldChange}
                      disabled={!isEditMode}
                      className="border border-gray-300 rounded p-1 w-full"
                    />
                  </div>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
